/**
 * The "your address was just proven" marker the api puts on the verification link's continue URL.
 *
 * Plain TypeScript — this module imports NO React, because reading the marker and deciding what the
 * address bar should say afterwards are rules, not rendering.
 *
 * <b>Why a marker exists at all.</b> Redeeming the emailed link flips `email_verified` at Firebase,
 * but the tab that was already signed in keeps the ID token it was issued, and the api promotes the
 * account only when a REFRESHED token arrives. Without a signal the user comes back to the same
 * "verify your email" callout and an unchanged account row, for up to the token's lifetime — which is
 * the live gap this exists to close. The marker tells the returning browser to force one token
 * refresh and re-read `/account`, which is the request that promotes.
 *
 * <b>The parameter is MIRRORED, not shared.</b> `EmailAddressVerificationService` in
 * `kitecorp/transferify-api-service` mints `…/account?emailVerified=return`; the two repos cannot
 * share a constant, so `verify-email-link.spec.ts` (the full-local-stack journey, which reads the link
 * the real api actually minted) is what proves they still agree. Reading it here rather than in a
 * component is also what lets a ui build that predates the api's parameter behave exactly as it did:
 * an unrecognised query parameter is simply not a marker.
 */

/** The query parameter the api's continue URL carries. */
export const VERIFIED_RETURN_PARAM = "emailVerified";

/**
 * The only value that counts as the marker.
 *
 * Matched exactly rather than by presence: `emailVerified` is also the name of the `/account` trust
 * FIELD (`account/emailTrust`), so a bare presence check would read a link someone hand-edited to
 * `?emailVerified=false` as "you just verified" and spend a token refresh saying so.
 */
export const VERIFIED_RETURN_VALUE = "return";

/**
 * Whether a location's query string carries the marker.
 *
 * @param search a `location.search` value, with or without the leading `?`
 */
export function isVerifiedReturn(search: string): boolean {
	return new URLSearchParams(search).get(VERIFIED_RETURN_PARAM) === VERIFIED_RETURN_VALUE;
}

/** The parts of a location this module rewrites — `window.location` satisfies it structurally. */
export interface AddressBarLocation {
	readonly pathname: string;
	readonly search: string;
	readonly hash: string;
}

/**
 * The same location with the marker removed, ready for `history.replaceState`.
 *
 * Everything else survives: the path, any other query parameter (a `?ref=` referral link that
 * happened to be on the same address is not ours to drop), and the fragment. A marker left in the
 * address bar would be bookmarked, shared and — because it is the trigger — re-run one forced token
 * refresh on every later visit to that URL.
 */
export function withoutVerifiedReturn(location: AddressBarLocation): string {
	const parameters = new URLSearchParams(location.search);
	parameters.delete(VERIFIED_RETURN_PARAM);
	const query = parameters.toString();
	return `${location.pathname}${query === "" ? "" : `?${query}`}${location.hash}`;
}
